<script>
import { computed, reactive, toRefs, ref } from 'vue'
import { useStore } from 'vuex'
import { routeTo } from '@/utils'
import SrUpload from '@/components/SrUpload'
import { addPersonRegInfo, getPersonRegInfo, updatePersonRegInfo } from "@/api/modules/auth"
import fileComp from '@/compositions/modules/file'
import ResetAuthTypeDialog from '@/components/ResetAuthTypeDialog'
import CertTypeSelect from "./components/cert-type-select"
import { vab } from "@libs/utils"
import { pinyin } from 'pinyin-pro'
import { useI18n } from "@/plugins/i18n"

export default {
  name: 'AuthRegister',
  components: { SrUpload, ResetAuthTypeDialog, CertTypeSelect },
  setup() {
    const store = useStore()
    const { t } = useI18n()
    const { FILE } = store.state.base.constant
    const nextOrUpdate = computed(() => +store.state.auth.step !== 2 ? 'next' : 'update')
    const { template } = fileComp()
    const refs = reactive({ formRef: null })
    const certRef = ref(null)

    // 国家/地区列表
    const areaCountryList = store.state.base.dict.country.countryCodeList

    // form相关代码
    const { formReq, loadingScope, formRules, handleToEname } = handleForm()

    // 处理下一步
    const handleNext = async () => {
      const stepComplete = store.state.auth.registerInfo?.stepComplete || null
      const regType = store.state.auth.registerInfo?.regType || null

      refs.formRef.validate(async (valid) => {
        if (valid) {
          // 验证cert-type-select组件表单
          console.log(certRef.value)
          const certValid = await certRef.value.validateForm()
          if (!certValid) return

          // 从cert-type-select组件获取文件ID
          const { backFileId, frontFileId, holdFileId,certType } = certRef.value.form
          const { regEname, regAddress, regEAddress, regName, countryCode, regId } = formReq
          
          const param = {
            idType: certType,
            backFileId,
            frontFileId,
            holdFileId,
            userId: store.state.user.userId,
            regEname,
            regAddress,
            regEAddress,
            regName,
            countryCode,
          }
          console.log(param)

          try {
            regId ? (await updatePersonRegInfo({ ...param, regId })) : (await addPersonRegInfo(param))
            loadingScope.btnLoading = false
            routeTo(((+stepComplete === 1 || nextOrUpdate.value === 'update') && (+regType !== 2)) ? '/auth/personal/overview' : '/auth/personal/businessInfo')
          } catch (err) {
            loadingScope.btnLoading = false
            console.log(err)
          }
        }
      })
    }

    return {
      FILE,
      formReq,
      formRules,
      areaCountryList,
      t,
      ...toRefs(refs),
      ...toRefs(loadingScope),
      ...toRefs(template),
      ...toRefs(initDialog()),
      handleToEname,
      handleNext,
      certRef
    }
  }
}

// form相关
const handleForm = () => {
  const store = useStore()
  const { t } = useI18n()
  const loadingScope = reactive({
    pageLoading: false,
    btnLoading: false
  })

  const formReq = reactive({
    acctType: '1', // 注册类型(1:个人 2:公司)
    backFileId: "", // 证件反面文件
    backFileName: "", // 证件反面文件名字
    frontFileId: "", // 证件正面文件
    frontFileName: "", // 证件正面文件名称
    holdFileId: "", // 手持照片文件
    holdFileName: "", // 手持照片文件名字
    userId: "", // 会员用户ID
    regEname: "", // 姓名拼音
    regAddress: "", // 常住地址
    regEAddress: "", // 常住英文地址
    regName: "", // 个人:真实姓名 企业:法人姓名，授权人姓名
    countryCode: "", // 证件签发地
    idType: "I", 
  })

  const validateEnText = (rule, value, callback) => {
    // [\x00-\xff] 代表 0 - 255的 ASCII 码值，是单字节的，非就是大于255的ASCII码，是双字节的
    const reg = /[^\x00-\xff]/g
    if (reg.test(value)) {
      callback(new Error(t('auth.company.info.error.text_1')))
    } else {
      callback()
    }
  }

  const validateEnName = (rule, value, callback) => {
    if (!value) {
      callback(new Error(t('auth.personal.register.error.text_5')))
    } else {
      validateEnText(rule, value, callback)
    }
  }

  const formRules = reactive({
    acctType: [
      { required: true, message: t('auth.personal.register.error.text_6'), trigger: 'change' }
    ],
    regName: [
      { required: true, message: t('auth.personal.register.placeholder.text_1'), trigger: 'blur' }
    ],
    regEname: [
      { required: true, message: t('auth.personal.register.placeholder.text_2'), trigger: 'blur' }
    ],
    regAddress: [
      { required: true, message: t('auth.personal.register.error.text_4'), trigger: 'blur' }
    ],
    countryCode: [
      { required: true, message: t('auth.company.register.error.issuingCountry'), trigger: 'change' }
    ],
  })

  // 处理自动转化拼音
  const handleToEname = (c) => {
    switch (c) {
      case 'regEname':
        formReq.regEname = pinyin(formReq.regName ? formReq.regName : '', { toneType: 'none', type: 'array' }).join('')
        break
      case 'regEAddress':
        formReq.regEAddress = pinyin(formReq.regAddress ? formReq.regAddress : '', { toneType: 'none', type: 'array' }).join('')
        break
    }
  }

  // 初始化
  const fetchAuthData = async () => {
    loadingScope.pageLoading = true
    try {
      const res = await getPersonRegInfo()
      store.dispatch('auth/setStep', res.regName ? false : '0')
      if (res.regName) {
        formReq.backFileId = res.backFileId
        formReq.frontFileId = res.frontFileId
        formReq.holdFileId = res.holdFileId
        formReq.regAddress = res.regAddress
        formReq.regCheckStatus = res.regCheckStatus
        formReq.regEAddress = res.regEAddress
        formReq.regEname = res.regEname
        formReq.regId = res.regId
        formReq.regName = res.regName
        formReq.regCheckStatus = res.regCheckStatus
        formReq.countryCode = res.countryCode || "CN"
        formReq.idType = res.idType
      }
      loadingScope.pageLoading = false
    } catch (err) {
      loadingScope.pageLoading = false
      console.log(err)
    }
  }

  fetchAuthData()

  return {
    formReq,
    loadingScope,
    formRules,
    handleToEname,
  }
}

// 处理弹框
const initDialog = () => {
  const dialogVisible = ref(false)
  return { dialogVisible }
}
</script>

<template>
  <div class="personal-auth-register-container">
    <el-form
        :model="formReq"
        :rules="formRules"
        ref="formRef"
        :disabled="formReq.regCheckStatus && ['CF', 'CP'].includes(formReq.regCheckStatus)"
        :label-width="localCN?'150px':'230px'"
        label-position="left"
        v-loading="pageLoading">
      <el-row>
        <el-col :span="24">
          <div class="h1">{{t('auth.personal.register.title.text_1')}}</div>

          <!--用户类型-->
          <el-form-item prop="userType" :label="t('auth.personal.register.label.text_1')">
            <el-radio-group v-model="formReq.acctType">
              <el-radio-button label="1">{{t('auth.personal.register.label.text_1_1')}}</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <!--真实姓名-->
          <el-form-item prop="regName" :label="t('auth.personal.register.label.text_2')">
            <el-input :placeholder="t('auth.personal.register.placeholder.text_1')" v-model="formReq.regName"/>
          </el-form-item>

          <!--姓名拼音-->
          <el-form-item prop="regEname" :label="t('auth.personal.register.label.text_3')">
            <el-input :placeholder="t('auth.personal.register.placeholder.text_2')" @focus="handleToEname('regEname')" v-model="formReq.regEname"/>
          </el-form-item>

          <!--证件签发地-->
          <el-form-item prop="countryCode" :label="t('auth.company.register.label.country')">
            <el-select
                v-model="formReq.countryCode"
                filterable>
              <el-option
                  v-for="country in areaCountryList"
                  :key="country.value"
                  :label="country.label"
                  :value="country.value">
                </el-option>
            </el-select>
          </el-form-item>

          <!--证件类型选择-->
          <cert-type-select 
            ref="certRef"
            :scopeData="{
              holdInterval: false,
              certType: formReq.idType,
              userType: 'L',
              frontFileId: formReq.frontFileId,
              compUserId: null,
              backFileId: formReq.backFileId,
              holdFileId: formReq.holdFileId,
            }"
          />

          <!--上传身份证-->
          <!-- <el-form-item :label="t('auth.personal.register.label.text_4')" :rules="{required: true}">
            <div class="auth-upload-container">
              <div class="upload-title-wrap">
                <div class="h3" style="height: 40px;line-height: 40px;">{{t('auth.personal.register.content.text_1')}}
                  <a class="a" style="color: #333;" download="授权书模板.pdf" href="javascript: void(0);"> {{t('auth.personal.register.content.text_1_2')}}</a>
                </div>
                <div class="h2">{{t('auth.personal.register.content.text_2')}}</div>
              </div>
              <div class="auth-upload-wrap">

                <div class="auth-upload-item">
                  <el-form-item class="upload-item" prop="frontFileId" :rules="{required: true, message: t('auth.personal.register.error.text_1'), trigger:'change'}">
                    <sr-upload
                        v-model:fileId="formReq.frontFileId"
                        v-model:fileName="formReq.frontFileName"
                        :filePrefix="FILE.PREFIX.AUTH_FILE"
                        fileSize="5"
                        :uploadDesc="t('auth.company.register.placeholder.text_4')"
                        :fileExample="idCardFront"
                        :mode="FILE.UPLOAD_MODE.EDITABLE"/>
                  </el-form-item>
                  <div class="h3">{{t('auth.company.register.content.text_3')}}</div>
                </div>

                <div class="auth-upload-item">
                  <el-form-item class="upload-item" prop="backFileId" :rules="{required: true, message: t('auth.personal.register.error.text_2'), trigger:'change'}">
                    <sr-upload
                        v-model:fileId="formReq.backFileId"
                        v-model:fileName="formReq.backFileName"
                        :filePrefix="FILE.PREFIX.AUTH_FILE"
                        fileSize="5"
                        :uploadDesc="t('auth.company.register.placeholder.text_5')"
                        :fileExample="idCardBack"
                        :mode="FILE.UPLOAD_MODE.EDITABLE"/>
                  </el-form-item>
                  <div class="h3">{{t('auth.company.register.content.text_4')}}</div>
                </div>

                <div class="auth-upload-item">
                  <el-form-item class="upload-item" prop="holdFileId" :rules="{required: true, message: t('auth.personal.register.error.text_3'), trigger:'change'}">
                    <sr-upload
                        v-model:fileId="formReq.holdFileId"
                        v-model:fileName="formReq.holdFileName"
                        :filePrefix="FILE.PREFIX.AUTH_FILE"
                        fileSize="5"
                        :uploadDesc="t('auth.personal.register.placeholder.text_5')"
                        :fileExample="idCardHold"
                        :mode="FILE.UPLOAD_MODE.EDITABLE"/>
                  </el-form-item>
                  <div class="h3">{{t('auth.personal.register.content.text_5')}}</div>
                </div>

              </div>
            </div>
          </el-form-item> -->

          <!--常住地址-->
          <el-form-item prop="regAddress" :label="t('auth.personal.register.label.text_5')">
            <el-input :placeholder="t('auth.personal.register.placeholder.text_6')" :type="localCN?'input':'textarea'" v-model="formReq.regAddress"/>
          </el-form-item>

          <!--常住地址拼音-->
          <el-form-item prop="regEAddress" :label="t('auth.personal.register.label.text_6')">
            <el-input :placeholder="t('auth.personal.register.placeholder.text_6')" :type="localCN?'input':'textarea'" @focus="handleToEname('regEAddress')" v-model="formReq.regEAddress"/>
          </el-form-item>

        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-form-item>
            <el-button
                class="el-button__next"
                type="primary"
                v-loading="btnLoading"
                :disabled="btnLoading"
                @click="handleNext">{{t('auth.personal.register.content.text_6')}}
            </el-button>
            <div class="a" style="margin-left: 50px;" @click="dialogVisible = true">{{t('auth.personal.register.content.text_7')}}</div>
          </el-form-item>
        </el-col>
      </el-row>
      <reset-auth-type-dialog :viewVisible="dialogVisible" @close="dialogVisible = false"/>
    </el-form>
  </div>
</template>
<style lang="scss">
  .personal-auth-register-container {
    padding: 20px;
    background-color: #fff;

    .auth-upload-container {
      width: 100%;
      box-sizing: border-box;

      .auth-upload-wrap {
        width: 100%;
        display: flex;

        .auth-upload-item {
          flex: 1;
          margin-right: 60px;

          .upload-item {
            margin-bottom: 15px;
          }
        }
      }
    }
  }
  .el-form-item__content {
    flex: inherit;

    .el-input__inner, .el-textarea__inner {
      width: 360px;
      border-radius: 4px !important;
    }
    .el-select{
      width: 390px !important;
      border-radius: 4px !important;
    }
    .el-input__wrapper{
      width: 100%;
    }
  }

</style>
